import React, {useMemo, useEffect} from 'react';
import {Link as RouterLink, useHistory, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import {makeStyles} from '@mui/styles';
import Box from '@mui/material/Box';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';
import {useDispatch} from 'react-redux';
import HomeIcon from '../../../../assets/svgs/HomeIcon';
import MyOrders from '../../../../assets/svgs/MyOrders';
import MyAssets from '../../../../assets/svgs/MyAssets';
import MyPayments from '../../../../assets/svgs/MyPayments';

import {colorIcon, setData} from '../../../../actions';

const ACTIVE_COLOR = '#0052FF';
const INACTIVE_COLOR = '#667085';

/**
 * Match a nav href to the current pathname.
 * `/payments` (Overview) and `/dashboard` are exact-only.
 * Other hrefs also match nested paths (e.g. `/payments/analytics/credit-utilisation`).
 */
const isNavHrefActive = (href, pathname) => {
    if (!href || !pathname) return false;
    if (pathname === href) return true;
    if (href === '/payments' || href === '/dashboard') return false;
    return pathname.startsWith(`${href}/`);
};

const getPaymentsNavigationPath = (location) => {
    if (!location?.pathname?.startsWith('/payments/partner/')) {
        return location?.pathname;
    }

    const originTrail = location.state?.originTrail || [];
    const sourcePath = [...originTrail]
        .reverse()
        .map((item) => item?.path)
        .find((path) => path?.startsWith('/payments/analytics') || path === '/payments/activity' || path === '/payments');

    return sourcePath || '/payments';
};

const useStyles = makeStyles(() => ({
    container: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1100,
        padding: '12px 14px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        pointerEvents: 'none'
    },
    // ---- main 4-item bar (Home / Refuel / Usage / Payment) ----
    bar: {
        width: '100%',
        maxWidth: 520,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.18)',
        padding: '10px 12px',
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 8,
        pointerEvents: 'auto'
    },
    item: {
        textDecoration: 'none',
        color: INACTIVE_COLOR,
        borderRadius: 12,
        padding: '8px 8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: 52
    },
    itemActive: {
        color: ACTIVE_COLOR,
        backgroundColor: '#EEF2F6'
    },
    label: {
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: 700
    },
    // ---- expanded state: Home breaks into its own circle ----
    homeCircle: {
        flex: '0 0 auto',
        width: 56,
        height: 56,
        borderRadius: '50%',
        backgroundColor: '#FFFFFF',
        boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        pointerEvents: 'auto'
    },
    // ---- sub-tab pill (Overview / Analytics / Activity) ----
    subBar: {
        flex: '1 1 auto',
        maxWidth: 440,
        backgroundColor: '#FFFFFF',
        borderRadius: 999,
        boxShadow: '0px 10px 30px rgba(15, 23, 42, 0.18)',
        padding: '10px 20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        pointerEvents: 'auto'
    },
    subItem: {
        textDecoration: 'none',
        color: INACTIVE_COLOR,
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4
    },
    subItemActive: {
        color: ACTIVE_COLOR
    },
    subItemIcon: {
        width: 20,
        height: 20,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
}));

const DEFAULT_NAV_ITEMS = [
    {
        id: 'home',
        label: 'Home',
        activeName: 'Home',
        to: '/dashboard',
        icon: HomeIcon
    },
    {
        id: 'procurement',
        label: 'Refuel',
        activeName: 'My Orders',
        to: '/myOrders',
        icon: MyOrders
    },
    {
        id: 'operations',
        label: 'Usage',
        activeName: 'My Assets',
        to: '/newasset',
        icon: MyAssets
    },
    {
        id: 'payments',
        label: 'Payment',
        activeName: 'My Payments',
        to: '/payments',
        icon: MyPayments,
        subItems: [
            {id: 'pay-overview', label: 'Overview', to: '/payments', icon: '/static/icons/payments/overviewMobileIcon.svg'},
            {id: 'pay-analytics', label: 'Analytics', to: '/payments/analytics', icon: '/static/icons/payments/analyticsMobileIcon.svg'},
            {id: 'pay-activity', label: 'Activity', to: '/payments/activity', icon: '/static/icons/payments/activityMobileIcon.svg'}
        ]
    }
];

const Bottom = ({
                    onMobileClose,
                    openMobile,
                    play,
                    items: itemsProp,
                    ...rest
                }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const paymentsNavigationPath = getPaymentsNavigationPath(location);
    const classes = useStyles();

    const navItems = useMemo(() => {
        if (Array.isArray(itemsProp) && itemsProp.length) return itemsProp;
        return DEFAULT_NAV_ITEMS;
    }, [itemsProp]);

    const paymentsItem = useMemo(
        () => navItems.find((item) => item.subItems && item.subItems.length),
        [navItems]
    );

    const homeItem = useMemo(
        () => navItems.find((item) => item.id === 'home') || navItems[0],
        [navItems]
    );

    const isPaymentsSection = Boolean(
        paymentsItem && location.pathname.startsWith('/payments')
    );

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const handleNavClick = (title) => {
        dispatch(colorIcon(title));
        dispatch(setData({
            OpenDetails: false,
            isDatum: false,
            isDashboard: false,
            datumData: []
        }));
    };

    const handlePaymentsClick = (e) => {
        handleNavClick(paymentsItem.activeName);
        // If we're not already inside /payments, land on the first sub-tab.
        if (!location.pathname.startsWith('/payments')) {
            e.preventDefault();
            history.push(paymentsItem.subItems[0].to);
        }
    };

    // ---------- Expanded: Home circle + Payments sub-tab pill ----------
    if (isPaymentsSection) {
        const HomeIconCmp = homeItem?.icon;

        return (
            <Hidden lgUp>
                <Box className={classes.container} {...rest}>
                    <Box
                        component={RouterLink}
                        to={homeItem.to}
                        className={classes.homeCircle}
                        onClick={() => handleNavClick(homeItem.activeName)}
                    >
                        {HomeIconCmp ? (
                            <HomeIconCmp size="20" colorCode={INACTIVE_COLOR} />
                        ) : null}
                    </Box>

                    <Box className={classes.subBar}>
                        {paymentsItem.subItems.map((sub) => {
                            const isSubActive = isNavHrefActive(sub.to, paymentsNavigationPath);
                            return (
                                <Box
                                    key={sub.id}
                                    component={RouterLink}
                                    to={sub.to}
                                    className={`${classes.subItem}${isSubActive ? ` ${classes.subItemActive}` : ''}`}
                                    data-track-event="bottom_navbar_clicked"
                                    data-track-props={JSON.stringify({tab_name: sub.label})}
                                    onClickCapture={play}
                                    onClick={() => handleNavClick(sub.label)}
                                >
                                    {sub.icon ? (
                                        <Box
                                            component="img"
                                            src={sub.icon}
                                            alt=""
                                            className={classes.subItemIcon}
                                            sx={{
                                                filter: isSubActive
                                                    ? 'invert(17%) sepia(99%) saturate(4000%) hue-rotate(218deg) brightness(100%) contrast(105%)'
                                                    : 'grayscale(1) opacity(0.65)'
                                            }}
                                        />
                                    ) : null}
                                    {sub.label}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Hidden>
        );
    }

    // ---------- Default: 4-item bar ----------
    return (
        <Hidden lgUp>
            <Box className={classes.container} {...rest}>
                <Box className={classes.bar}>
                    {navItems.map((item, index) => {
                        const Icon = item.icon;
                        const hasSubItems = item.subItems && item.subItems.length > 0;
                        const isActive =
                            location.pathname === item.to ||
                            (item.to !== '/dashboard' && location.pathname.startsWith(`${item.to}/`));

                        return (
                            <Box
                                key={item.id || item.to || index}
                                component={RouterLink}
                                to={item.to}
                                className={`${classes.item}${isActive ? ` ${classes.itemActive}` : ''}`}
                                data-track-event={item.id !== 'home' ? 'bottom_navbar_clicked' : undefined}
                                data-track-props={item.id !== 'home'
                                    ? JSON.stringify({tab_name: item.label})
                                    : undefined}
                                onClickCapture={play}
                                onClick={hasSubItems
                                    ? handlePaymentsClick
                                    : () => handleNavClick(item.activeName)}
                            >
                                <Box sx={{width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    {Icon ? (
                                        <Icon size="20" colorCode={isActive ? ACTIVE_COLOR : INACTIVE_COLOR} />
                                    ) : null}
                                </Box>
                                <Typography className={classes.label} sx={{color: isActive ? ACTIVE_COLOR : INACTIVE_COLOR}}>
                                    {item.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Hidden>
    );
};

Bottom.propTypes = {
    play: PropTypes.func,
    items: PropTypes.array
};

export default Bottom;
